// The two reads the web's identity gate makes (#764), and only for a uid whose
// token carries the portal claim: no teacher's session reaches either.
//
//   watchBillingPresence  whether `users/{uid}/billing/account` exists — the
//                         fact that makes a portal login also a studio.
//   listPortalMemberships the `portalMembers` rows naming the uid, for the
//                         /portal page: every studio and family it may see.
//
// Plain listeners rather than the read store's windows: neither is studio
// data, neither is written by this client, and the read store's scope is the
// studio's own collections (src/data/readStore.js).

import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { BILLING_DOCS, COLLECTIONS, docRef } from './paths';
import { BILLING_KNOWN } from '../domain/identity';

export const PORTAL_MEMBERS = 'portalMembers';

/**
 * Calls `onKnown('present' | 'absent')` as the account document is seen, and
 * returns the unsubscribe. A snapshot served from the cache counts: offline,
 * a device that has never read the document is told it is absent, which for a
 * portal login is the answer that writes nothing. A refused or failed read is
 * reported as absent too, for the same reason — and only a uid with the portal
 * claim is ever asked.
 */
export function watchBillingPresence(uid, onKnown) {
  const ref = docRef(uid, COLLECTIONS.billing, BILLING_DOCS.account);
  return onSnapshot(
    ref,
    (snap) => onKnown(snap.exists() ? BILLING_KNOWN.present : BILLING_KNOWN.absent),
    (err) => {
      console.warn('[identity] could not read the billing account; treating this portal login as having none:', err);
      onKnown(BILLING_KNOWN.absent);
    },
  );
}

/**
 * Every membership naming `uid`, as `{ id, studioUid, familyId, contactId,
 * studioName, familyName, status }`. The names are what the server wrote when
 * access was granted; either may be missing. `status` is `active` or
 * `revoked` (#770: revoking archives the membership rather than deleting
 * it); a row written before the invitations carried none, and is active.
 */
export async function listPortalMemberships(uid) {
  const snap = await getDocs(query(collection(db, PORTAL_MEMBERS), where('uid', '==', uid)));
  return snap.docs.map((d) => {
    const data = d.data() || {};
    const [studioFromId, familyFromId, contactFromId] = d.id.split('__');
    return {
      id: d.id,
      studioUid: typeof data.studioUid === 'string' ? data.studioUid : studioFromId || null,
      familyId: typeof data.familyId === 'string' ? data.familyId : familyFromId || null,
      contactId: typeof data.contactId === 'string' ? data.contactId : contactFromId || null,
      status: data.status === 'revoked' ? 'revoked' : 'active',
      studioName: typeof data.studioName === 'string' && data.studioName.trim() ? data.studioName.trim() : null,
      familyName: typeof data.familyName === 'string' && data.familyName.trim() ? data.familyName.trim() : null,
    };
  });
}
